import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaDownload,
  FaEdit,
  FaTrash,
  FaEye,
  FaCodeBranch,
  FaFolderOpen,
  FaFolder,
  FaTimes,
  FaExclamationTriangle,
  FaClock,
  FaBuilding,
  FaUserTie,
  FaUsers,
  FaTruck,
  FaExclamationCircle,
} from "react-icons/fa";
import api from "../api/axios";
import "./DocumentManagement.css";
import { formatFileSize, getFileIcon, getExpiryBadge, CATEGORIES, DOC_TYPES } from "./docUtils";

export default function DocumentList() {
  const navigate = useNavigate();

  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState({ total: 0, expiring_soon: 0, expired: 0, by_category: {}, by_type: {} });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [docType, setDocType] = useState("");
  const [expiryStatus, setExpiryStatus] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  // Delete modal
  const [deleteId, setDeleteId] = useState(null);
  const [deleteTitle, setDeleteTitle] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/document-management/stats/overview");
      setStats(res.data);
    } catch (e) {}
  }, []);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * LIMIT,
        limit: LIMIT,
      };
      if (search) params.search = search;
      if (category) params.category = category;
      if (docType) params.document_type = docType;
      if (expiryStatus) params.expiry_status = expiryStatus;

      const res = await api.get("/document-management/", { params });
      setDocs(res.data.data || []);
      setTotal(res.data.total || 0);
      setError("");
    } catch (e) {
      setError("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  }, [page, search, category, docType, expiryStatus]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setDocType("");
    setExpiryStatus("");
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/document-management/${deleteId}`);
      setDeleteId(null);
      setDeleteTitle("");
      fetchDocs();
      fetchStats();
    } catch (e) {
      setError("Failed to delete document.");
    } finally {
      setDeleting(false);
    }
  };

  const getTypeIcon = (type) => {
    if (type === "company") return <FaBuilding />;
    if (type === "employee") return <FaUserTie />;
    if (type === "customer") return <FaUsers />;
    if (type === "supplier") return <FaTruck />;
    return <FaFolder />;
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="doc-mgmt-container">
      {/* Header */}
      <div className="doc-mgmt-header">
        <div className="doc-header-left">
          <div className="doc-header-icon-box">
            <FaFolderOpen />
          </div>
          <div>
            <h1 className="doc-mgmt-title">Document Management</h1>
            <p className="doc-mgmt-subtitle">
              Centralized repository for corporate records, client files, and compliance documents
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="doc-btn-warning"
            onClick={() => navigate("/dashboard/documents/expiry-alerts")}
          >
            <FaExclamationCircle />
            <span>Expiry Alerts</span>
            {stats.expiring_soon + stats.expired > 0 && (
              <span className="badge bg-danger ms-1">{stats.expiring_soon + stats.expired}</span>
            )}
          </button>
          <button
            className="doc-btn-primary"
            onClick={() => navigate("/dashboard/documents/upload")}
          >
            <FaPlus />
            <span>Upload Document</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Cards (Professional SVG Icons) */}
      <div className="doc-stats-row">
        <div className="doc-stats-card">
          <div className="doc-stat-icon-wrap primary">
            <FaFolder />
          </div>
          <div className="doc-stat-content">
            <span className="doc-stat-value">{stats.total}</span>
            <span className="doc-stat-label">Total Documents</span>
          </div>
        </div>

        <div className="doc-stats-card">
          <div className="doc-stat-icon-wrap amber">
            <FaClock />
          </div>
          <div className="doc-stat-content">
            <span className="doc-stat-value" style={{ color: "#d97706" }}>{stats.expiring_soon}</span>
            <span className="doc-stat-label">Expiring Soon</span>
          </div>
        </div>

        <div className="doc-stats-card">
          <div className="doc-stat-icon-wrap rose">
            <FaExclamationTriangle />
          </div>
          <div className="doc-stat-content">
            <span className="doc-stat-value" style={{ color: "#e11d48" }}>{stats.expired}</span>
            <span className="doc-stat-label">Expired Documents</span>
          </div>
        </div>

        {Object.entries(stats.by_type || {}).map(([type, count]) => (
          <div key={type} className="doc-stats-card">
            <div className="doc-stat-icon-wrap slate">
              {getTypeIcon(type)}
            </div>
            <div className="doc-stat-content">
              <span className="doc-stat-value">{count}</span>
              <span className="doc-stat-label">{type} Files</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="doc-filter-bar">
        <div className="doc-filter-group" style={{ minWidth: 240, flex: 1 }}>
          <label>Search Query</label>
          <div style={{ position: "relative" }}>
            <FaSearch style={{ position: "absolute", left: 10, top: 11, color: "#94a3b8", fontSize: 12 }} />
            <input
              type="text"
              placeholder="Search by title, party name, or tags..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ paddingLeft: 30, width: "100%" }}
            />
          </div>
        </div>

        <div className="doc-filter-group">
          <label>Document Category</label>
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="doc-filter-group">
          <label>Classification Type</label>
          <select value={docType} onChange={e => { setDocType(e.target.value); setPage(1); }}>
            <option value="">All Classifications</option>
            <option value="company">Company</option>
            <option value="employee">Employee</option>
            <option value="customer">Customer</option>
            <option value="supplier">Supplier</option>
          </select>
        </div>

        <div className="doc-filter-group">
          <label>Compliance Status</label>
          <select value={expiryStatus} onChange={e => { setExpiryStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="expiring_soon">Expiring Soon</option>
            <option value="expired">Expired</option>
            <option value="no_expiry">No Expiry Date</option>
          </select>
        </div>

        {(search || category || docType || expiryStatus) && (
          <div className="doc-filter-group" style={{ minWidth: "auto", justifyContent: "flex-end" }}>
            <button className="doc-btn-outline" onClick={clearFilters}>
              <FaTimes />
              <span>Reset Filters</span>
            </button>
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Table */}
      <div className="doc-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: 56 }}>
            <div className="spinner-border text-primary" role="status" />
            <div style={{ marginTop: 14, color: "#64748b", fontSize: 13, fontWeight: 500 }}>
              Loading repository...
            </div>
          </div>
        ) : docs.length === 0 ? (
          <div className="doc-empty">
            <div className="doc-empty-icon">
              <FaFolderOpen />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#0f172a" }}>No documents found</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              Upload your first document or refine your search criteria.
            </div>
          </div>
        ) : (
          <table className="doc-table">
            <thead>
              <tr>
                <th>Document Name</th>
                <th>Category</th>
                <th>Classification</th>
                <th>Associated Party</th>
                <th>Date Uploaded</th>
                <th>Compliance Status</th>
                <th>File Size</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} onClick={() => navigate(`/dashboard/documents/${doc.id}`)}>
                  <td>
                    <div className="doc-title-cell">
                      {getFileIcon(doc.mime_type, doc.file_name)}
                      <div>
                        <div className="doc-title-text">{doc.title}</div>
                        <div className="doc-file-name">{doc.file_name}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="cat-badge">{doc.category}</span>
                  </td>
                  <td style={{ textTransform: "capitalize", fontSize: 12, fontWeight: 500 }}>
                    {doc.document_type}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {doc.entity_name ? (
                      <span>
                        <span style={{ color: "#64748b", textTransform: "capitalize" }}>{doc.entity_type}: </span>
                        <strong>{doc.entity_name}</strong>
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                    {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"}
                    {doc.uploaded_by && <div style={{ color: "#94a3b8", fontSize: 11 }}>{doc.uploaded_by}</div>}
                  </td>
                  <td>
                    {doc.has_expiry && doc.expiry_date ? (
                      <div>
                        {getExpiryBadge(doc.expiry_status)}
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                          {new Date(doc.expiry_date).toLocaleDateString()}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                    {formatFileSize(doc.file_size)}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="doc-actions-wrap" style={{ justifyContent: "flex-end" }}>
                      <button
                        className="doc-action-btn"
                        title="View Document Details"
                        onClick={() => navigate(`/dashboard/documents/${doc.id}`)}
                      >
                        <FaEye />
                      </button>
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="doc-action-btn success"
                        title="Download Original File"
                        onClick={e => e.stopPropagation()}
                      >
                        <FaDownload />
                      </a>
                      <button
                        className="doc-action-btn"
                        title="Edit Metadata"
                        onClick={() => navigate(`/dashboard/documents/edit/${doc.id}`)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="doc-action-btn"
                        title="Upload New Version"
                        onClick={() => navigate(`/dashboard/documents/${doc.id}?tab=versions`)}
                      >
                        <FaCodeBranch />
                      </button>
                      <button
                        className="doc-action-btn danger"
                        title="Delete Document"
                        onClick={() => { setDeleteId(doc.id); setDeleteTitle(doc.title); }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="doc-pagination">
            <button className="doc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              const pg = i + Math.max(1, page - 3);
              if (pg > totalPages) return null;
              return (
                <button
                  key={pg}
                  className={`doc-page-btn ${pg === page ? "active" : ""}`}
                  onClick={() => setPage(pg)}
                >
                  {pg}
                </button>
              );
            })}
            <button className="doc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </button>
            <span style={{ fontSize: 12, color: "#64748b", marginLeft: 10 }}>
              {total} total records
            </span>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="modal show d-block" style={{ background: "rgba(15, 23, 42, 0.6)" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title" style={{ fontSize: "16px", fontWeight: 700, color: "#dc2626" }}>
                  Delete Document
                </h5>
                <button className="btn-close" onClick={() => setDeleteId(null)} />
              </div>
              <div className="modal-body pt-2">
                <p style={{ fontSize: "13px", color: "#334155" }}>
                  Are you sure you want to delete <strong>{deleteTitle}</strong>?
                </p>
                <p className="text-muted" style={{ fontSize: "11px", margin: 0 }}>
                  This will archive the record from the repository.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="doc-btn-outline" onClick={() => setDeleteId(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  style={{ borderRadius: "8px", fontWeight: 600, padding: "8px 14px" }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
